import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { Employee } from '../types/employee';
import { TrendingUp, TrendingDown, Award, AlertTriangle, Users, Clock, Target, Activity, Info, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PerformanceMetrics {
  employeeId: string;
  employeeName: string;
  totalHours: number;
  totalWeeks: number;
  avgHoursPerWeek: number;
  hoursVariance: number;
  consistencyScore: number;
  contractorCount: number;
  totalTimesheets: number;
  lastWorkedDate: Date | null;
  performanceScore: number;
  trend: 'up' | 'down' | 'stable';
  weeklyHoursHistory: Array<{ week: string; hours: number }>;
  contractorBreakdown: Array<{ contractor: string; hours: number; percentage: number }>;
}

interface PerformanceAnalyticsProps {
  selectedEmployeeId?: string;
}

const EmployeePerformanceAnalytics: React.FC<PerformanceAnalyticsProps> = ({ 
  selectedEmployeeId
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceMetrics[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<'hours' | 'consistency' | 'performance'>('performance');
  const [timeRange, setTimeRange] = useState<'3months' | '6months' | '1year' | 'all'>('6months');
  const [selectedEmployee, setSelectedEmployee] = useState<PerformanceMetrics | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  useEffect(() => {
    fetchEmployeesAndPerformanceData();
  }, [timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEmployeesAndPerformanceData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch employees
      const employeesRef = collection(db, 'employees');
      const employeesQuery = query(employeesRef, orderBy('name'));
      const employeesSnapshot = await getDocs(employeesQuery);
      
      const employeesList: Employee[] = employeesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || '',
          jobTitle: data.jobTitle || '',
          hourlyWage: data.hourlyWage || 0,
          status: data.status || 'active',
          hireDate: data.hireDate?.toDate() || null,
          terminationDate: data.terminationDate?.toDate() || null,
          driverLicenseExpiry: data.driverLicenseExpiry?.toDate() || null,
          tdlrLicenseExpiry: data.tdlrLicenseExpiry?.toDate() || null,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          ...data
        } as Employee;
      });

      // Fetch timesheets data
      const timesheetsRef = collection(db, 'weeklyTimesheets');
      const timesheetsQuery = query(timesheetsRef, orderBy('weekEnding', 'desc'));
      const timesheetsSnapshot = await getDocs(timesheetsQuery);

      const timesheets = timesheetsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        weekEnding: doc.data().weekEnding?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));

      // Calculate performance metrics
      const performanceMetrics = calculatePerformanceMetrics(employeesList, timesheets);
      setPerformanceData(performanceMetrics);

    } catch (err) {
      console.error('Error fetching performance data:', err);
      setError('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const calculatePerformanceMetrics = (employees: Employee[], timesheets: any[]): PerformanceMetrics[] => {
    const cutoffDate = getTimeRangeCutoff();
    
    return employees.map(employee => {
      // Filter timesheets for this employee within time range
      const employeeTimesheets = timesheets.filter(timesheet => {
        if (timesheet.weekEnding < cutoffDate) return false;
        
        return timesheet.entries && timesheet.entries.some((entry: any) => 
          entry.employeeName?.toLowerCase() === employee.name.toLowerCase()
        );
      }).map(timesheet => {
        // Calculate employee hours for this timesheet
        const employeeEntries = timesheet.entries.filter((entry: any) => 
          entry.employeeName?.toLowerCase() === employee.name.toLowerCase()
        );
        
        const totalHours = employeeEntries.reduce((total: number, entry: any) => {
          return total + Object.values(entry.hours || {}).reduce((sum: number, hours: any) => sum + (Number(hours) || 0), 0);
        }, 0);

        return {
          ...timesheet,
          employeeHours: totalHours,
          employeeEntries: employeeEntries.length
        };
      }).filter(timesheet => timesheet.employeeHours > 0);

      // Group by week ending date and sum hours
      const weeklyHours = employeeTimesheets.reduce((acc, ts) => {
        const weekKey = ts.weekEnding.toDateString();
        if (!acc[weekKey]) {
          acc[weekKey] = { hours: 0, contractor: ts.contractorName || 'Unknown' };
        }
        acc[weekKey].hours += ts.employeeHours;
        return acc;
      }, {} as Record<string, { hours: number; contractor: string }>);

      const totalWeeks = Object.keys(weeklyHours).length;
      const totalHours: number = Object.values(weeklyHours).reduce((sum: number, week: { hours: number; contractor: string }) => sum + week.hours, 0);
      const avgHoursPerWeek = totalWeeks > 0 ? totalHours / totalWeeks : 0;

      // Calculate variance for consistency score
      const hoursList = Object.values(weeklyHours).map(week => week.hours);
      const variance = totalWeeks > 1 ? 
        hoursList.reduce((acc, hours) => acc + Math.pow(hours - avgHoursPerWeek, 2), 0) / (totalWeeks - 1) : 0;
      
      // Consistency score (higher is better, based on lower variance)
      const consistencyScore = variance > 0 ? Math.max(0, 100 - (Math.sqrt(variance) / avgHoursPerWeek) * 100) : 100;

      // Contractor diversity
      const contractors = new Set(employeeTimesheets.map(ts => ts.contractorName || 'Unknown'));
      const contractorBreakdown = Array.from(contractors).map(contractor => {
        const contractorHours = employeeTimesheets
          .filter(ts => (ts.contractorName || 'Unknown') === contractor)
          .reduce((sum, ts) => sum + ts.employeeHours, 0);
        return {
          contractor,
          hours: contractorHours,
          percentage: totalHours > 0 ? (contractorHours / totalHours) * 100 : 0
        };
      }).sort((a, b) => b.hours - a.hours);

      // Weekly hours history for trends
      const weeklyHoursHistory = Object.entries(weeklyHours)
        .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
        .slice(-12) // Last 12 weeks
        .map(([week, data]) => ({
          week: new Date(week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          hours: data.hours
        }));

      // Calculate trend (comparing last 4 weeks to previous 4 weeks)
      let trend: 'up' | 'down' | 'stable' = 'stable';
      if (weeklyHoursHistory.length >= 8) {
        const recent4 = weeklyHoursHistory.slice(-4);
        const previous4 = weeklyHoursHistory.slice(-8, -4);
        const recentAvg = recent4.reduce((sum, w) => sum + w.hours, 0) / 4;
        const previousAvg = previous4.reduce((sum, w) => sum + w.hours, 0) / 4;
        
        if (recentAvg > previousAvg * 1.1) trend = 'up';
        else if (recentAvg < previousAvg * 0.9) trend = 'down';
      }

      // Overall performance score (0-100) - More hours = higher score
      const hoursScore = Math.min(100, (avgHoursPerWeek / 50) * 100); // Based on 50h/week high target
      const activityScore = Math.min(100, (totalWeeks / 26) * 100); // Based on 26 weeks in 6 months
      const volumeBonus = totalHours > 1000 ? 10 : totalHours > 500 ? 5 : 0; // Bonus for high total hours
      const performanceScore = Math.min(100, (hoursScore * 0.5 + consistencyScore * 0.25 + activityScore * 0.25 + volumeBonus));

      const lastWorkedDate = employeeTimesheets.length > 0 ? 
        new Date(Math.max(...employeeTimesheets.map(ts => ts.weekEnding.getTime()))) : null;

      return {
        employeeId: employee.id,
        employeeName: employee.name || 'Unknown',
        totalHours,
        totalWeeks,
        avgHoursPerWeek,
        hoursVariance: variance,
        consistencyScore,
        contractorCount: contractors.size,
        totalTimesheets: employeeTimesheets.length,
        lastWorkedDate,
        performanceScore,
        trend,
        weeklyHoursHistory,
        contractorBreakdown
      };
    }).filter(metrics => metrics.totalHours > 0) // Only include employees with recorded hours
      .sort((a, b) => b.performanceScore - a.performanceScore); // Sort by performance score
  };

  const getTimeRangeCutoff = (): Date => {
    const now = new Date();
    switch (timeRange) {
      case '3months':
        return new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
      case '6months':
        return new Date(now.getTime() - (180 * 24 * 60 * 60 * 1000));
      case '1year':
        return new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
      case 'all':
      default:
        return new Date(0);
    }
  };

  const getPerformanceBadgeColor = (score: number): string => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const handleEmployeeClick = (employee: PerformanceMetrics) => {
    setSelectedEmployee(employee);
    setShowDetailModal(true);
  };

  // Filter employees based on search query
  const filteredEmployees = performanceData.filter(employee =>
    employee.employeeName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredEmployees.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 text-lg">Loading performance analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Performance Data</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Employee Performance Analytics</h1>
            <p className="text-blue-100 mt-1">Data-driven insights into workforce performance and productivity</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/employees/performance/methodology')}
              className="flex items-center space-x-2 bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded-lg transition-colors text-sm"
              title="Learn how performance scores are calculated"
            >
              <Info className="h-4 w-4" />
              <span>How it's calculated</span>
            </button>
            <Activity className="h-12 w-12 text-blue-200" />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as any)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="3months">Last 3 Months</option>
                <option value="6months">Last 6 Months</option>
                <option value="1year">Last Year</option>
                <option value="all">All Time</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Metric</label>
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value as any)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="performance">Overall Performance</option>
                <option value="hours">Average Hours/Week</option>
                <option value="consistency">Consistency Score</option>
              </select>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="text-sm text-gray-600">
              {searchQuery ? `${filteredEmployees.length} of ${performanceData.length}` : `${performanceData.length}`} active employees
            </div>
          </div>
        </div>
      </div>

      {/* Performance Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Award className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Top Performer</p>
              <p className="text-2xl font-semibold text-gray-900">
                {performanceData.length > 0 ? performanceData[0].employeeName : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Hours/Week</p>
              <p className="text-2xl font-semibold text-gray-900">
                {performanceData.length > 0 ? 
                  (performanceData.reduce((sum, p) => sum + p.avgHoursPerWeek, 0) / performanceData.length).toFixed(1) : '0'
                }h
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Target className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">High Performers</p>
              <p className="text-2xl font-semibold text-gray-900">
                {performanceData.filter(p => p.performanceScore >= 80).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Employees</p>
              <p className="text-2xl font-semibold text-gray-900">{performanceData.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Leaderboard */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">Performance Leaderboard</h3>
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredEmployees.length)} of {filteredEmployees.length}
              {searchQuery && ` (filtered from ${performanceData.length})`}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance Score
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Hours/Week
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Consistency
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contractors
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedEmployees.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    {searchQuery ? `No employees found matching "${searchQuery}"` : 'No employees found'}
                  </td>
                </tr>
              ) : (
                paginatedEmployees.map((employee, index) => {
                  const globalIndex = startIndex + index;
                  return (
                <tr 
                  key={employee.employeeId} 
                  className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                  onClick={() => handleEmployeeClick(employee)}
                  title="Click to view detailed performance analysis"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {globalIndex < 3 && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mr-2 ${
                          globalIndex === 0 ? 'bg-yellow-100 text-yellow-800' :
                          globalIndex === 1 ? 'bg-gray-100 text-gray-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          #{globalIndex + 1}
                        </span>
                      )}
                      {globalIndex >= 3 && (
                        <span className="text-sm text-gray-500 font-medium">#{globalIndex + 1}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{employee.employeeName}</div>
                    <div className="text-sm text-gray-500">
                      {employee.totalWeeks} weeks • {employee.totalHours.toFixed(0)} total hours
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPerformanceBadgeColor(employee.performanceScore)}`}>
                      {employee.performanceScore.toFixed(0)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 font-medium">
                    {employee.avgHoursPerWeek.toFixed(1)}h
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {employee.consistencyScore.toFixed(0)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {employee.trend === 'up' && <TrendingUp className="h-4 w-4 text-green-600 mx-auto" />}
                    {employee.trend === 'down' && <TrendingDown className="h-4 w-4 text-red-600 mx-auto" />}
                    {employee.trend === 'stable' && <div className="w-4 h-4 mx-auto bg-gray-300 rounded-full"></div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {employee.contractorCount}
                  </td>
                </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {filteredEmployees.length > pageSize && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredEmployees.length)} of {filteredEmployees.length} results
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-200 transition-colors'
                  }`}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let pageNumber;
                    if (totalPages <= 7) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 4) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 3) {
                      pageNumber = totalPages - 6 + i;
                    } else {
                      pageNumber = currentPage - 3 + i;
                    }
                    
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageClick(pageNumber)}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          currentPage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-200 transition-colors'
                  }`}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Employee Detail Modal */}
      {showDetailModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    Performance Analysis
                  </h3>
                  <p className="text-purple-100 text-sm mt-1">
                    {selectedEmployee.employeeName} • {selectedEmployee.performanceScore.toFixed(0)} Performance Score
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-purple-200 hover:text-white transition-colors duration-200 text-2xl font-light"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-6">
              {/* Key Metrics Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">
                    {selectedEmployee.avgHoursPerWeek.toFixed(1)}h
                  </div>
                  <div className="text-sm text-blue-600 font-medium">Avg Hours/Week</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">
                    {selectedEmployee.consistencyScore.toFixed(0)}%
                  </div>
                  <div className="text-sm text-green-600 font-medium">Consistency</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-700">
                    {selectedEmployee.totalWeeks}
                  </div>
                  <div className="text-sm text-purple-600 font-medium">Weeks Active</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-700">
                    {selectedEmployee.contractorCount}
                  </div>
                  <div className="text-sm text-orange-600 font-medium">Contractors</div>
                </div>
              </div>

              {/* Performance Trend */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Performance Trend</h4>
                  <div className="flex items-center space-x-2">
                    {selectedEmployee.trend === 'up' && <TrendingUp className="h-5 w-5 text-green-600" />}
                    {selectedEmployee.trend === 'down' && <TrendingDown className="h-5 w-5 text-red-600" />}
                    {selectedEmployee.trend === 'stable' && <div className="w-5 h-5 bg-gray-300 rounded-full"></div>}
                    <span className={`text-sm font-medium ${
                      selectedEmployee.trend === 'up' ? 'text-green-600' :
                      selectedEmployee.trend === 'down' ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                      {selectedEmployee.trend === 'up' ? 'Improving' :
                       selectedEmployee.trend === 'down' ? 'Declining' : 'Stable'}
                    </span>
                  </div>
                </div>
                
                {selectedEmployee.weeklyHoursHistory.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Weekly Hours Over Time (Last 12 Weeks)</span>
                    </div>
                    <div className="h-32 bg-gray-50 rounded flex items-end justify-center p-2 space-x-1">
                      {selectedEmployee.weeklyHoursHistory.map((week, index) => (
                        <div key={index} className="flex-1 flex flex-col items-center">
                          <div 
                            className="bg-blue-500 rounded-sm w-full transition-all duration-300 hover:bg-blue-600"
                            style={{ height: `${Math.max(4, (week.hours / Math.max(...selectedEmployee.weeklyHoursHistory.map(w => w.hours))) * 80)}px` }}
                            title={`${week.week}: ${week.hours}h`}
                          ></div>
                          <span className="text-xs text-gray-500 mt-1 transform -rotate-45 origin-left">
                            {week.week}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Contractor Breakdown */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Work Distribution by Contractor</h4>
                <div className="space-y-3">
                  {selectedEmployee.contractorBreakdown.map((contractor, index) => (
                    <div key={contractor.contractor} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          index === 0 ? 'bg-blue-500' :
                          index === 1 ? 'bg-green-500' :
                          index === 2 ? 'bg-purple-500' :
                          'bg-gray-400'
                        }`}></div>
                        <span className="font-medium text-gray-900">{contractor.contractor}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">{contractor.hours.toFixed(1)}h</div>
                        <div className="text-sm text-gray-500">{contractor.percentage.toFixed(1)}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Performance Insights */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      selectedEmployee.performanceScore >= 80 ? 'bg-green-500' :
                      selectedEmployee.performanceScore >= 60 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}></div>
                    <div>
                      <span className="font-medium text-gray-900">Overall Performance: </span>
                      <span className={
                        selectedEmployee.performanceScore >= 80 ? 'text-green-600' :
                        selectedEmployee.performanceScore >= 60 ? 'text-yellow-600' :
                        'text-red-600'
                      }>
                        {selectedEmployee.performanceScore >= 80 ? 'Excellent' :
                         selectedEmployee.performanceScore >= 60 ? 'Good' : 'Needs Improvement'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      selectedEmployee.avgHoursPerWeek >= 45 ? 'bg-green-500' :
                      selectedEmployee.avgHoursPerWeek >= 35 ? 'bg-blue-500' :
                      selectedEmployee.avgHoursPerWeek >= 25 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}></div>
                    <div>
                      <span className="font-medium text-gray-900">Work Volume: </span>
                      <span className="text-gray-600">
                        {selectedEmployee.avgHoursPerWeek >= 45 ? 'High productivity - excellent revenue generation' :
                         selectedEmployee.avgHoursPerWeek >= 35 ? 'Good productivity - solid revenue contributor' :
                         selectedEmployee.avgHoursPerWeek >= 25 ? 'Moderate productivity - room for growth' :
                         'Low productivity - underutilized capacity'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      selectedEmployee.consistencyScore >= 80 ? 'bg-green-500' :
                      selectedEmployee.consistencyScore >= 60 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}></div>
                    <div>
                      <span className="font-medium text-gray-900">Reliability: </span>
                      <span className="text-gray-600">
                        {selectedEmployee.consistencyScore >= 80 ? 'Very consistent work patterns' :
                         selectedEmployee.consistencyScore >= 60 ? 'Moderately consistent' :
                         'Irregular work patterns'}
                      </span>
                    </div>
                  </div>

                  {selectedEmployee.lastWorkedDate && (
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 rounded-full mt-2 bg-blue-500"></div>
                      <div>
                        <span className="font-medium text-gray-900">Last Active: </span>
                        <span className="text-gray-600">
                          {selectedEmployee.lastWorkedDate.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowDetailModal(false)}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeePerformanceAnalytics;